import React from 'react'

import { Box, Stack, Text } from '@chakra-ui/layout'
import {
  ButtonGroup,
  Button as ChakraButton,
  IconButton,
} from '@chakra-ui/button'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/modal'
import { useToast } from '@chakra-ui/toast'
import { useDisclosure } from '@chakra-ui/hooks'

import { useMutation } from '@tanstack/react-query'

import { useTranslation } from 'react-i18next'

import { FiTrash2 } from 'react-icons/fi'

import { axios } from '../../../../../../services/axios'

import { Button } from '../../../../../../components/Button'

import { formatErrorMessage } from '../../../../../../helpers/errors'
import { queryClient } from '../../../../../../services/react-query'

type ModalRemoveAccreditedProps = {
  original: {}
}

export function ModalRemoveAccredited({
  original,
}: ModalRemoveAccreditedProps) {
  const toast = useToast()
  const { t } = useTranslation()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const removeAccredited = useMutation(
    async () => {
      await axios().patch(`/accredited`, {
        ...original,
        has_deleted: true,
      })
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['accredited']),
    }
  )

  async function handleDeleteAccredited(): Promise<void> {
    try {
      await removeAccredited.mutateAsync()

      onClose()

      toast({
        description: t('accredited_delete_description'),
        status: 'success',
        position: 'top-right',
        variant: 'left-accent',
        isClosable: true,
      })
    } catch (err) {
      const message = formatErrorMessage(err as Error)

      !toast.isActive(message) &&
        toast({
          id: message,
          title: t('toast_error_title'),
          description: t(message),
          status: 'error',
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
        })
    }
  }

  const cancelRef = React.useRef<HTMLButtonElement>(null)

  return (
    <Box>
      <IconButton
        aria-label="Apagar"
        icon={<FiTrash2 />}
        background="transparent"
        _hover={{
          background: 'transparent',
          color: 'parkoff.orange.300',
        }}
        onClick={() => onOpen()}
      />
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isOpen={isOpen}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader />
          <AlertDialogCloseButton />
          <AlertDialogBody marginTop="1rem">
            <Stack spacing="1rem">
              <Text fontSize="lg" fontWeight="bold" textAlign="center">
                Tem certeza que deseja remover este credenciado?
              </Text>
              <Text textAlign="center">Esta ação não poderá ser desfeita.</Text>
            </Stack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <ButtonGroup
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              marginY="1rem"
            >
              <ChakraButton
                fontSize="xs"
                fontWeight="normal"
                color="#5F5F5F"
                width="4.9375rem"
                height="1.8125rem"
                backgroundColor="transparent"
                borderRadius="lg"
                _hover={{
                  backgroundColor: 'transparent',
                }}
                onClick={onClose}
                ref={cancelRef}
              >
                Voltar
              </ChakraButton>
              <Button
                type="submit"
                width="11.5625rem"
                onClick={handleDeleteAccredited}
              >
                Remover
              </Button>
            </ButtonGroup>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  )
}
